import {Component, computed, inject, input, signal} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Panel} from 'primeng/panel';
import {SelectButton} from 'primeng/selectbutton';
import {ProgressBarComponent} from '../../../../../../../shared/components/progress-bar/progress-bar.component';
import {
  benchmarkPosition,
  BenchmarkPosition,
  careRatio,
  categoryFte,
  categoryHeadcount,
  headcountWithoutHr,
  otherHeadcount,
  sumValues,
  unitKpis,
} from '../company-info.calc';
import {
  CompanyInfoValues,
  CONTINGENT_CATEGORY,
  HR_CATEGORY,
  INDUSTRIAL_CATEGORY,
  SALARIED_CATEGORY,
  UnitSummary,
  WorkforceCategoryDef,
} from '../step-1-1-company-information.types';
import {WorkforceKpiCardComponent} from '../workforce-kpi-card/workforce-kpi-card.component';
import {BENCHMARK_REFERENCES, BENCHMARK_SOURCES, CARE_RATIO_BENCHMARK} from './benchmark.data';
import {BenchmarkRayComponent} from './benchmark-ray/benchmark-ray.component';
import {WorkforceDonutChartComponent} from './workforce-donut-chart/workforce-donut-chart.component';
import {DonutSegment, DonutValueMode} from './workforce-donut-chart/workforce-donut-chart.types';

interface ModeOption {
  value: DonutValueMode;
  labelKey: string;
}

const POSITION_LABEL_KEY: Record<BenchmarkPosition, string> = {
  below: 'project.steps.companyInfo.careRatioBelow',
  within: 'project.steps.companyInfo.careRatioWithin',
  above: 'project.steps.companyInfo.careRatioAbove',
};

const CATEGORY_COLOR_VARS = [
  '--color-criteria-1',
  '--color-criteria-2',
  '--color-criteria-3',
  '--color-criteria-4',
  '--color-criteria-5',
  '--color-criteria-6',
  '--color-criteria-7',
  '--color-criteria-8',
  '--color-criteria-9',
];

function colorVarAt(index: number): string {
  return CATEGORY_COLOR_VARS[index % CATEGORY_COLOR_VARS.length];
}

@Component({
  selector: 'app-company-summary',
  imports: [
    TranslocoPipe,
    AppNumberPipe,
    FormsModule,
    SelectButton,
    Panel,
    ProgressBarComponent,
    WorkforceKpiCardComponent,
    WorkforceDonutChartComponent,
    BenchmarkRayComponent,
  ],
  templateUrl: './company-summary.component.html',
  styleUrl: './company-summary.component.scss',
})
export class CompanySummaryComponent {
  private readonly translocoService = inject(TranslocoService);

  readonly units = input.required<UnitSummary[]>();

  protected readonly modeOptions: ModeOption[] = [
    {value: 'absolute', labelKey: 'project.steps.companyInfo.viewAbsolute'},
    {value: 'percent', labelKey: 'project.steps.companyInfo.viewPercent'},
  ];
  protected readonly benchmarkReferences = BENCHMARK_REFERENCES;
  protected readonly benchmarkSources = BENCHMARK_SOURCES;
  protected readonly typicalRangeParams = {
    min: CARE_RATIO_BENCHMARK.typicalMin,
    max: CARE_RATIO_BENCHMARK.typicalMax,
  };

  protected readonly mode = signal<DonutValueMode>('absolute');
  protected readonly benchmarksCollapsed = signal(true);

  private readonly language = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  protected readonly totals = computed(() => sumValues(this.units().map((unit) => unit.values)));
  protected readonly kpis = computed(() => unitKpis(this.totals()));
  protected readonly unitCount = computed(() => this.units().length);
  protected readonly completedUnits = computed(
    () => this.units().filter((unit) => unit.status === 'complete').length,
  );
  protected readonly headcountByCategory = computed<DonutSegment[]>(() => {
    this.language();
    const totals = this.totals();
    return [
      this.categorySegment(SALARIED_CATEGORY, categoryHeadcount(totals, SALARIED_CATEGORY), 0),
      this.categorySegment(INDUSTRIAL_CATEGORY, categoryHeadcount(totals, INDUSTRIAL_CATEGORY), 1),
      this.categorySegment(CONTINGENT_CATEGORY, categoryHeadcount(totals, CONTINGENT_CATEGORY), 2),
      this.otherSegment(totals),
    ];
  });
  protected readonly fteByCategory = computed<DonutSegment[]>(() => {
    this.language();
    const totals = this.totals();
    return [
      this.categorySegment(SALARIED_CATEGORY, categoryFte(totals, SALARIED_CATEGORY), 0),
      this.categorySegment(INDUSTRIAL_CATEGORY, categoryFte(totals, INDUSTRIAL_CATEGORY), 1),
      this.categorySegment(CONTINGENT_CATEGORY, categoryFte(totals, CONTINGENT_CATEGORY), 2),
    ];
  });
  protected readonly headcountByUnit = computed<DonutSegment[]>(() =>
    this.units().map((unit, index) =>
      this.unitSegment(unit, headcountWithoutHr(unit.values), index),
    ),
  );
  protected readonly hrByUnit = computed<DonutSegment[]>(() =>
    this.units().map((unit, index) =>
      this.unitSegment(unit, categoryHeadcount(unit.values, HR_CATEGORY), index),
    ),
  );
  protected readonly careRatio = computed(() => careRatio(this.totals()));
  protected readonly hasCareRatio = computed(() => this.careRatio() !== null);
  protected readonly careRatioValue = computed(() => this.careRatio() ?? 0);
  protected readonly careRatioPositionKey = computed(() => {
    const ratio = this.careRatio();
    if (ratio === null) {
      return '';
    }
    const position = benchmarkPosition(
      ratio,
      CARE_RATIO_BENCHMARK.typicalMin,
      CARE_RATIO_BENCHMARK.typicalMax,
    );
    return POSITION_LABEL_KEY[position];
  });

  onModeChange(mode: DonutValueMode): void {
    this.mode.set(mode);
  }

  onBenchmarksCollapsedChange(collapsed: boolean | undefined): void {
    this.benchmarksCollapsed.set(collapsed ?? true);
  }

  private categorySegment(
    category: WorkforceCategoryDef,
    value: number,
    colorIndex: number,
  ): DonutSegment {
    return {
      key: category.key,
      label: this.translocoService.translate(category.labelKey),
      value,
      colorVar: colorVarAt(colorIndex),
    };
  }

  private otherSegment(totals: CompanyInfoValues): DonutSegment {
    return {
      key: 'other',
      label: this.translocoService.translate('project.steps.companyInfo.categoryOther'),
      value: otherHeadcount(totals),
      colorVar: colorVarAt(3),
    };
  }

  private unitSegment(unit: UnitSummary, value: number, index: number): DonutSegment {
    return {
      key: String(unit.office.id),
      label: unit.office.name,
      value,
      colorVar: colorVarAt(index),
    };
  }
}
